import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';

/**
 * Datadog RUM configuration, kept apart from the React component so the
 * privacy guarantees can be unit tested:
 *
 * - Session replay is OFF. Replay records the DOM, and `mask-user-input`
 *   does not mask things like the medical screening Yes/No buttons.
 * - Everything is masked (`defaultPrivacyLevel: 'mask'`), including the
 *   text used to name click actions.
 * - No URL that carries a secret token ever leaves the browser: every URL
 *   field Datadog lets us edit runs through `redactUrl` in `beforeSend`.
 *
 * Basic error and performance RUM stays on: it is how we see broken
 * checkouts and slow pages, and none of it needs page content.
 */

export const REDACTED = '[redacted]';

/**
 * Page and API routes whose path carries a secret (magic link, access
 * token, invite). Each regex captures the prefix; the next path segment is
 * the secret and is replaced. Anchored to the path start, so `/api/...`
 * routes are listed separately.
 */
const TOKEN_PATH_PATTERNS: RegExp[] = [
  // Athlete-facing pages
  /^(\/my-ticket\/)[^/]+/,
  /^(\/medical-screening\/)(?!preview(?:\/|$))[^/]+/,
  /^(\/staff\/(?:i|ask|unsubscribe)\/)[^/]+/,
  /^(\/accept-invitation\/)[^/]+/,
  /^(\/claim-(?:raffle|spot|ticket)\/)[^/]+/,
  /^(\/fundraising\/setup\/)[^/]+/,
  /^(\/gift\/manage\/)[^/]+/,
  /^(\/gift\/)(?!manage(?:\/|$))[^/]+/,
  /^(\/join-team\/)[^/]+/,
  /^(\/photo-upload\/)[^/]+/,
  /^(\/photos\/event\/)[^/]+/,
  /^(\/photos\/)(?!event(?:\/|$))[^/]+/,
  /^(\/spectator\/)[^/]+/,
  /^(\/split-pay\/)[^/]+/,
  /^(\/unsubscribe\/)[^/]+/,
  // API routes those pages call
  /^(\/api\/claim-ticket\/)[^/]+/,
  /^(\/api\/gift-(?:claim|manage)\/)[^/]+/,
  /^(\/api\/medical-screening\/public\/)[^/]+/,
  /^(\/api\/photographer-portal\/)[^/]+/,
  /^(\/api\/public\/claim\/(?:cancellation|raffle)\/)[^/]+/,
  /^(\/api\/public\/crew\/)[^/]+/,
  /^(\/api\/race-photos\/event\/)[^/]+/,
  // An album id opens a public album's selfie search and purchase just like
  // the secret /photos/<albumToken> link, so it is treated as a secret too.
  /^(\/api\/race-photos\/)(?!event(?:\/|$))[^/]+/,
  /^(\/api\/team\/invitation\/)[^/]+/,
];

/**
 * Defence in depth for token routes we have not listed (including the
 * Express API on Render): a long run of URL-safe characters with letters
 * and digits that is not a hyphenated lowercase slug looks like a secret
 * (nanoid, hex, base64url). Event slugs and UUIDs are kept, so dashboard
 * URLs stay useful for debugging.
 */
function looksLikeSecret(segment: string): boolean {
  if (segment.length < 16) return false;
  // Standard base64 (+ and =) as well as base64url and nanoid alphabets.
  if (!/^[A-Za-z0-9_+=-]+$/.test(segment)) return false;
  if (!/[0-9]/.test(segment) || !/[A-Za-z]/.test(segment)) return false;
  const isLowercaseSlug = /^[a-z0-9]+(?:-[a-z0-9]+)+$/.test(segment);
  return !isLowercaseSlug;
}

function redactPath(path: string): string {
  let out = path;
  for (const pattern of TOKEN_PATH_PATTERNS) {
    out = out.replace(pattern, `$1${REDACTED}`);
  }
  return out
    .split('/')
    .map((segment) => {
      let decoded = segment;
      try {
        decoded = decodeURIComponent(segment);
      } catch {
        // Keep the raw segment; it is still tested below.
      }
      return looksLikeSecret(decoded) ? REDACTED : segment;
    })
    .join('/');
}

/**
 * Redacts a URL (absolute or relative) for telemetry:
 * - token route segments and secret-looking segments become `[redacted]`;
 * - every query value becomes `[redacted]` (keys are kept, so a
 *   `?ticket=` or Stripe `payment_intent_client_secret` never leaks, but
 *   we can still see which parameters a page was opened with);
 * - the hash is dropped.
 */
export function redactUrl(url: string): string {
  if (!url) return url;
  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const queryIndex = withoutHash.indexOf('?');
  const beforeQuery = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1);

  // Split off scheme and host so only the path is redacted.
  const originMatch = beforeQuery.match(/^([a-z][a-z0-9+.-]*:\/\/[^/]*)(.*)$/i);
  const origin = originMatch ? originMatch[1] : '';
  const path = originMatch ? originMatch[2] : beforeQuery;

  const redactedQuery = query
    ? '?' +
      query
        .split('&')
        .filter(Boolean)
        .map((pair) => {
          const eq = pair.indexOf('=');
          // A bare `?<value>` has no key worth keeping: it may be the secret.
          if (eq === -1) return REDACTED;
          return `${pair.slice(0, eq)}=${REDACTED}`;
        })
        .join('&')
    : '';

  return `${origin}${redactPath(path)}${redactedQuery}`;
}

/**
 * Redacts every URL or path found inside free text (error messages and
 * stacks). A stack frame's trailing `:line:column` is kept, so Datadog can
 * still map the frame to source.
 */
export function redactUrlsInText(text: string): string {
  if (!text) return text;
  return text.replace(/(?:[a-z][a-z0-9+.-]*:\/\/[^\s'"<>()]+|\/[^\s'"<>()]+)/gi, (match) => {
    const position = match.match(/(?::\d+){1,2}$/);
    if (!position) return redactUrl(match);
    return redactUrl(match.slice(0, -position[0].length)) + position[0];
  });
}

/**
 * Runs before every RUM event leaves the browser. Only fields Datadog marks
 * as editable are touched; edits to anything else are silently dropped by
 * the SDK. Always returns true (events are redacted, not discarded), so
 * error and performance monitoring keeps working on token pages.
 */
export function rumBeforeSend(event: RumEvent): boolean {
  const e = event as RumEvent & {
    view: { url: string; referrer?: string; name?: string; performance?: { lcp?: { resource_url?: string } } };
    resource?: { url?: string };
    error?: { message?: string; stack?: string; handling_stack?: string; resource?: { url?: string } };
    long_task?: { scripts?: Array<{ source_url?: string; invoker?: string }> };
  };

  if (e.view) {
    if (e.view.url) e.view.url = redactUrl(e.view.url);
    if (e.view.referrer) e.view.referrer = redactUrl(e.view.referrer);
    if (e.view.name) e.view.name = redactUrl(e.view.name);
    const lcp = e.view.performance?.lcp;
    if (lcp?.resource_url) lcp.resource_url = redactUrl(lcp.resource_url);
  }
  if (e.type === 'resource' && e.resource?.url) {
    e.resource.url = redactUrl(e.resource.url);
  }
  if (e.type === 'error' && e.error) {
    if (e.error.resource?.url) e.error.resource.url = redactUrl(e.error.resource.url);
    if (e.error.message) e.error.message = redactUrlsInText(e.error.message);
    // A V8 stack starts with "Error: <message>", so it carries the same URLs.
    if (e.error.stack) e.error.stack = redactUrlsInText(e.error.stack);
    if (e.error.handling_stack) e.error.handling_stack = redactUrlsInText(e.error.handling_stack);
  }
  if (e.type === 'long_task' && e.long_task?.scripts) {
    for (const script of e.long_task.scripts) {
      if (script.source_url) script.source_url = redactUrl(script.source_url);
      if (script.invoker) script.invoker = redactUrlsInText(script.invoker);
    }
  }
  return true;
}

export interface RumEnv {
  applicationId: string;
  clientToken: string;
  site?: string;
  service?: string;
  env: string;
  version: string;
}

/** Init options for `datadogRum.init`, minus plugins (added by the caller). */
export function buildRumInitConfiguration(input: RumEnv): RumInitConfiguration {
  return {
    applicationId: input.applicationId,
    clientToken: input.clientToken,
    site: input.site ?? 'datadoghq.eu',
    service: input.service ?? 'fitvent-web',
    env: input.env,
    version: input.version,
    sessionSampleRate: 100,
    // Session replay is off. It records page content (including answers on
    // the medical screening form), which we must not send to a third party.
    sessionReplaySampleRate: 0,
    startSessionReplayRecordingManually: true,
    defaultPrivacyLevel: 'mask',
    enablePrivacyForActionName: true,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    beforeSend: rumBeforeSend,
    // iOS Safari blocks the default session cookie under ITP because the
    // Datadog intake is cross-site relative to fitvent.com. Partitioned
    // cookies (CHIPS) are the supported workaround on iOS 16.4+.
    usePartitionedCrossSiteSessionCookie: true,
    // Smaller intake payloads, which matters on slow mobile uplinks.
    compressIntakeRequests: true,
  };
}
